import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistForm({
  compact = false,
  showNameField = false,
  ctaLabel,
  successMessage,
  stacked = false,
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Bitte trag deine E-Mail-Adresse ein.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        name: name.trim() || null,
      });
      setSuccess(true);
      toast.success("Du bist dabei. Wir schreiben dir bald ❤");
      setEmail("");
      setName("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast("Du stehst schon auf unserer Liste — bleib dran.", {
          description: "Deine Einladung kommt, sobald wir öffnen.",
        });
      } else if (Array.isArray(detail)) {
        toast.error("Bitte gib eine gültige E-Mail-Adresse ein.");
      } else {
        toast.error(detail || "Da ist etwas schiefgelaufen. Bitte versuch es noch einmal.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center gap-3 rounded-full border border-qh-rose/30 bg-[#FFE4E6]/50 px-5 py-3 w-fit"
        data-testid="waitlist-success"
      >
        <CheckCircle2 className="w-5 h-5 text-qh-rose" strokeWidth={2} />
        <span className="text-sm font-medium text-qh-ink">
          {successMessage || "Du bist dabei. Wir schreiben dir."}
        </span>
        <button
          onClick={() => setSuccess(false)}
          className="text-xs text-qh-slate underline underline-offset-2 hover:text-qh-ink"
          data-testid="waitlist-add-another"
        >
          Weitere eintragen
        </button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`flex ${stacked ? "flex-col" : "flex-col sm:flex-row"} gap-3 w-full max-w-lg`}
      data-testid="waitlist-form"
    >
      {showNameField ? (
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Vorname (optional)"
          className="h-12 rounded-full border-qh-line bg-white px-5 text-base focus-visible:ring-2 focus-visible:ring-qh-rose focus-visible:ring-offset-0 focus-visible:border-qh-rose"
          data-testid="waitlist-name-input"
        />
      ) : (
        !compact && (
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Dein Name (optional)"
            className="hidden"
            data-testid="waitlist-name-input"
            aria-hidden
          />
        )
      )}
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="deine@email.de"
        required
        className="h-12 rounded-full border-qh-line bg-white px-5 text-base focus-visible:ring-2 focus-visible:ring-qh-rose focus-visible:ring-offset-0 focus-visible:border-qh-rose"
        data-testid="waitlist-email-input"
      />
      <Button
        type="submit"
        disabled={loading}
        className="h-12 rounded-full bg-qh-rose hover:bg-qh-rosedark text-white px-6 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0 whitespace-nowrap"
        data-testid="waitlist-submit"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Einen Moment…
          </>
        ) : (
          <>
            {ctaLabel || "Auf die Warteliste"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </form>
  );
}
